import { FLIGHT_SEARCH_STORAGE } from '@config/storageKeys';
import { flightClasses } from '@v2/views/flights/components/FlightsFilters/data';
import type {
  Airport,
  Flight,
  JourneyType,
  Passengers,
} from '@v2/views/flights/types';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { createJSONStorage, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
// Separate state interface from actions for better type safety
interface FlightSearchStateValues {
  // Search parameters
  hasSubmitted: boolean;
  connectionsAccepted: 0 | 1;
  originAirport: Airport | null;
  destinationAirport: Airport | null;
  departureDate: string;
  returnDate?: string;
  journeyType: JourneyType;
  cabinClass: string;
  passengers: Passengers;

  // UI state

  sortBy: string;
  // Selected flights
  flightsSelected: Flight[];
}

// Actions interface
interface FlightSearchActions {
  // Search actions
  updateFlightSearch: (
    values: Partial<Omit<FlightSearchStateValues, 'flightsSelected'>>
  ) => void;

  // Date selection actions
  handleDateRangeChange: (from: Date | undefined, to: Date | undefined) => void;
  clearDates: () => void;

  // Flight selection actions
  addFlightSelected: (flight: Flight) => void;
  removeFlightSelected: (index: number) => void;
  clearSelectedFlights: () => void;
  setSortBy: (sortBy: string) => void;
  initFlightSearch: (flightDetails: Partial<FlightSearchStateValues>) => void;
  clearFlightParams: () => void;
}

// Combined state and actions
export interface FlightSearchState
  extends FlightSearchStateValues, FlightSearchActions {}

// Initial state definition
const initialState: FlightSearchStateValues = {
  sortBy: 'price-low-high',
  hasSubmitted: false,
  flightsSelected: [],
  connectionsAccepted: 0,
  originAirport: null,
  destinationAirport: null,
  departureDate: '',
  returnDate: '',
  journeyType: 'round-trip',
  cabinClass: flightClasses[0].value,
  passengers: {
    adults: 2,
    children: 0,
    infants: 0,
  },
};

// Create store with immer and persist middleware for localStorage sync
export const useFlightSearchStore = create<FlightSearchState>()(
  persist(
    immer((set) => ({
      ...initialState,

      handleDateRangeChange: (from: Date | undefined, to: Date | undefined) =>
        set((state) => {
          // If both dates are the same in round-trip mode, clear the dates
          if (
            state.journeyType === 'round-trip' &&
            from &&
            to &&
            from.getTime() === to.getTime()
          ) {
            state.departureDate = '';
            state.returnDate = '';
            return;
          }

          // Format dates to string (assuming we want to store as ISO string)
          state.departureDate = from ? from.toISOString() : '';
          state.returnDate =
            state.journeyType === 'round-trip' && to ? to.toISOString() : '';
        }),

      clearDates: () =>
        set((state) => {
          state.departureDate = '';
          state.returnDate = '';
        }),

      initFlightSearch: (flightDetails) =>
        set((state) => {
          Object.assign(state, flightDetails);
        }),

      clearFlightParams: () =>
        set((state) => {
          Object.assign(state, initialState);
        }),

      setSortBy: (sortBy) =>
        set((state) => {
          state.sortBy = sortBy;
        }),
      // Flight selection actions
      addFlightSelected: (flight) =>
        set((state) => {
          state.flightsSelected.push(flight);
        }),
      removeFlightSelected: (index) =>
        set((state) => {
          state.flightsSelected = state.flightsSelected.filter(
            (_, i) => i !== index
          );
        }),

      clearSelectedFlights: () =>
        set((state) => {
          state.flightsSelected = [];
        }),

      // Search actions
      updateFlightSearch: (values) =>
        set((state) => {
          Object.assign(state, values);
          state.hasSubmitted = true;
        }),
    })),
    {
      name: FLIGHT_SEARCH_STORAGE, // unique name for localStorage key
      storage: createJSONStorage(() =>
        typeof window !== 'undefined' ? localStorage : (null as any)
      ),
      partialize: (state) => ({
        flightsSelected: state.flightsSelected,
        // Only persist these fields
        originAirport: state.originAirport,
        destinationAirport: state.destinationAirport,
        departureDate: state.departureDate,
        returnDate: state.returnDate,
        journeyType: state.journeyType,
        cabinClass: state.cabinClass,
        passengers: state.passengers,
        connectionsAccepted: state.connectionsAccepted,
        sortBy: state.sortBy,
        hasSubmitted: state.hasSubmitted,
      }),
    }
  )
);

// Create a selector to get all search params
export const getFlightSearchParams = (state: FlightSearchState) => ({
  originAirport: state.originAirport,
  destinationAirport: state.destinationAirport,
  departureDate: state.departureDate,
  returnDate: state.returnDate,
  adults: state.passengers.adults,
  children: state.passengers.children,
  infants: state.passengers.infants,
  cabinClass: state.cabinClass,
});

// Additional selectors for common use cases
export const useFlightSelection = () =>
  useFlightSearchStore(
    useShallow((state) => ({
      flightsSelected: state.flightsSelected,
      addFlight: state.addFlightSelected,
      removeFlight: state.removeFlightSelected,
      clearFlights: state.clearSelectedFlights,
    }))
  );

export const useFlightSearchParams = () =>
  useFlightSearchStore(
    useShallow((state) => ({
      journeyType: state.journeyType,
      originAirport: state.originAirport,
      destinationAirport: state.destinationAirport,
      departureDate: state.departureDate,
      returnDate: state.returnDate,
      cabinClass: state.cabinClass,
      passengers: state.passengers,
      updateSearch: state.updateFlightSearch,
      connectionsAccepted: state.connectionsAccepted,
    }))
  );

export const useFlightTotalPassengers = () =>
  useFlightSearchStore(
    (state) =>
      state.passengers.adults +
      state.passengers.children +
      state.passengers.infants
  );
